import { Form, Input, Select, Button, Row, Col, DatePicker, TimePicker, Modal } from "antd";
import PropTypes from "prop-types";
import SaveScheduleModal from "../Modal/SaveSchedule";
import { useApplicantTrackingById } from "../../../app/hooks/useApplicantTracking";
import { useApplicantTracking } from "../../../app/hooks/useApplicantTracking";
import { useCurrentUser } from "../../../app/hooks/useCurrentUser";
import { useState, useEffect, useCallback } from "react";
import { PaginatedResult } from "../../../app/utils/pagination";
import { useStore } from "../../../app/stores/store";
import { useInterview } from "../../../app/hooks/useInterview";
import { observer } from "mobx-react-lite";
import { disabledDate, disabledTime, getDisabledEndTime } from "../../../app/utils/dateTimeUtils";
import { useInterviewPaginatedSelect } from "../../../app/hooks/useInterviewPaginatedSelect";

const { Option } = Select;
const { TextArea } = Input;
const pageSize = 10;

const ScheduleInterviewForm = ({ onClose, onSave }) => {
  const { interviewStore, notificationStore } = useStore();
  const { showNotification } = notificationStore;
  const { handleFinalSubmit, loading } = interviewStore;
  const interviewAPI = useInterview();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [applicantId, setApplicantId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSave, setIsSave] = useState(true);
  const {
    currentUser: { user },
  } = useCurrentUser();
  const { data: selectedApplicant } = useApplicantTrackingById(applicantId, { enabled: !!applicantId });

  const handleSubmit = async () => {
    const values = form.getFieldsValue(true);
    const success = await handleFinalSubmit(values, interviewAPI, user.id);

    if (success) {
      setIsModalVisible(false);
      onClose();
    }
  };

  const handleApproveClick = async () => {
    try {
      await form.validateFields();

      setIsSave(true);
      setIsModalVisible(true);
    } catch (errorInfo) {
      showNotification("Fill in required fields." || errorInfo, "error", {
        title: `Error saving schedule`,
        duration: 5000,
      });
    }
  };

  const handleDeclineClick = () => {
    setIsSave(false);
    setIsModalVisible(true);
  };

  const handleDiscard = () => {
    setIsModalVisible(false);
    onClose();
  };

  const [form] = Form.useForm();

  const { getApplicantTracking } = useApplicantTracking({
    pageSize: pageSize,
  });

  const fetchApplicants = useCallback(
    async (pageNumber, pageSize, selectSearch = "") => {
      setIsLoading(true);

      try {
        const result = await getApplicantTracking(pageNumber, pageSize, selectSearch);
        return result;
      } catch (error) {
        showNotification(error, "error", {
          title: "Error loading applicants",
          duration: 5000,
        });
        return new PaginatedResult();
      } finally {
        setIsLoading(false);
      }
    },
    [getApplicantTracking, showNotification],
  );

  const { options: applicantOptions, onPopupScroll, onSearch } = useInterviewPaginatedSelect(fetchApplicants, pageSize);

  useEffect(() => {
    if (selectedApplicant && applicantId) {
      form.setFieldsValue({
        position: selectedApplicant.jobPost?.title || "",
        jobOpeningId: selectedApplicant.jobPostId || "",
      });
    }
  }, [selectedApplicant, applicantId, form]);

  const handleCandidateChange = (newApplicantId) => {
    setApplicantId(newApplicantId);
    if (!newApplicantId) {
      form.setFieldsValue({
        position: "",
        jobOpeningId: "",
      });
    }
  };

  const handleDateChange = () => {
    form.setFieldsValue({ startTime: null, endTime: null });
  };

  const getEndTimeConstraints = () => {
    const selectedDate = form.getFieldValue("scheduledDate");
    const startTime = form.getFieldValue("startTime");
    return getDisabledEndTime(selectedDate, startTime);
  };

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onSave} requiredMark={false}>
        <Form.Item label="Candidate" name="applicantId" rules={[{ required: true, message: "Please select an applicant" }]}>
          <Select
            showSearch
            loading={isLoading}
            placeholder="Select applicant"
            onChange={handleCandidateChange}
            onPopupScroll={onPopupScroll}
            onSearch={onSearch}
            filterOption={false}
            options={[
              { value: undefined, label: "Select Candidate", disabled: true },
              ...(applicantOptions?.map((applicant) => ({
                value: applicant.id,
                label: applicant.fullName,
              })) || []),
            ]}
          />
        </Form.Item>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Position" name="position">
              <Input readOnly placeholder="Job Position" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Type" name="type" rules={[{ required: true, message: "Please select interview type" }]}>
              <Select placeholder="Select type of interview">
                <Option value="InitialInterview">Initial Interview</Option>
                <Option value="TechnicalInterview">Technnical Interview</Option>
                <Option value="PanelInterview">Panel Interview</Option>
                <Option value="HRInterview">HR Interview</Option>
                <Option value="FinalInterview">Final Interview</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Date" name="scheduledDate" rules={[{ required: true, message: "Please select a date" }]}>
              <DatePicker
                placeholder="Pick a date"
                className=" w-full"
                format="dddd - MMMM DD"
                disabledDate={disabledDate}
                onChange={handleDateChange}
                popupClassName="custom-datepicker-popup"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <div className="flex gap-4">
              <Form.Item label="Time" name="startTime" className="flex-1" rules={[{ required: true, message: "Start time is required" }]}>
                <TimePicker
                  hideDisabledOptions
                  use12Hours
                  format="h:mm A"
                  placeholder="Start"
                  className="w-full"
                  disabledTime={() => disabledTime(form.getFieldValue("scheduledDate"))}
                />
              </Form.Item>
              <Form.Item label=" " name="endTime" className="flex-1" rules={[{ required: true, message: "End time si required" }]}>
                <TimePicker hideDisabledOptions use12Hours format="h:mm A" placeholder="End" className="w-full" disabledTime={getEndTimeConstraints} />
              </Form.Item>
            </div>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="On-Site Location" name="onSiteLocation">
              <Input placeholder="Please enter your location" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Online Link" name="onlineLink">
              <Input placeholder="Online Interview URL" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Note" name="notes">
          <TextArea rows={4} placeholder="Write a note..." maxLength={1000} className="resize-none" />
        </Form.Item>

        <div className="flex justify-end gap-4 mt-8">
          <Button
            size="large"
            className="w-40 !border-orange-500 !text-orange-500 !hover:text-orange-700 !hover:border-orange-700  hover:opacity-70 font-semibold rounded-lg"
            onClick={handleDeclineClick}
          >
            CANCEL
          </Button>

          <Button
            type="primary"
            size="large"
            className="w-40 border-none font-semibold rounded-lg hover:opacity-80"
            onClick={handleApproveClick}
            style={{ backgroundColor: "#F45F0C" }}
          >
            SAVE
          </Button>
        </div>
      </Form>
      <Modal open={isModalVisible} centered onCancel={() => setIsModalVisible(false)} footer={null} width={400} destroyOnClose>
        <SaveScheduleModal
          onCancel={() => setIsModalVisible(false)}
          onSaveSuccess={handleSubmit}
          isSave={isSave}
          onDiscard={handleDiscard}
          loading={loading && isSave}
        />
      </Modal>
    </>
  );
};

ScheduleInterviewForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default observer(ScheduleInterviewForm);
